import React from 'react';
import { Controller, useFormContext, FieldValues, Path } from 'react-hook-form';
import { Box, Autocomplete, TextField, FormControl } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import { DynamicDownChevron } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/DynamicDownChevron';

export type DropdownOption = {
  value: string;
  disabled?: boolean;
};

type ControlledDropdownProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[] | DropdownOption[];
  placeholder: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: Record<string, any>;
  getMappingOption?: (value: string) => string;
  error?: boolean;
};

export const ControlledDropdown: React.FC<ControlledDropdownProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  fullWidth = true,
  size = 'small',
  sx,
  getMappingOption,
  error = false
}) => {
  // Convert options to format expected by Autocomplete
  const autocompleteOptions = options.map((option) => {
    const optionValue = typeof option === 'string' ? option : option.value;
    const isDisabled = typeof option === 'string' ? false : option.disabled || false;
    const display = getMappingOption ? getMappingOption(optionValue) : optionValue;

    return {
      value: optionValue,
      label: display,
      disabled: isDisabled
    };
  });

  // Find the current option object
  const currentOption = autocompleteOptions.find((option) => option.value === value) || null;

  return (
    <Box sx={{ flexDirection: 'column', ...sx }}>
      {label ? (
        <Text variant="body2" sx={{ fontWeight: 500, marginBottom: '8px', display: 'block' }}>
          {label}
        </Text>
      ) : null}
      <Box
        sx={{
          border: `2px solid ${error ? colors.red : colors.borderGrey}`,
          borderRadius: '4px',
          width: '100%',
          backgroundColor: 'white',
          '&:focus-within': {
            borderColor: error ? colors.red : colors.darkerGrey
          }
        }}
      >
        <FormControl
          fullWidth={fullWidth}
          size={size}
          sx={{
            margin: 0,
            '& .MuiInputBase-root': {
              margin: 0
            }
          }}
        >
          <Autocomplete
            size={size}
            fullWidth={fullWidth}
            options={autocompleteOptions}
            value={currentOption}
            onChange={(_, newValue) => {
              onChange(newValue?.value || '');
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            getOptionDisabled={(option) => option.disabled || false}
            popupIcon={<DynamicDownChevron color={colors.labelGrey} style={{}} />}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                sx={{
                  '& .MuiAutocomplete-clearIndicator': {
                    display: 'none'
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    top: '50% !important',
                    transform: 'translateY(-50%)'
                  },
                  '& .MuiInputBase-root': {
                    height: '40px',
                    padding: '0 16px',
                    fontSize: '14px',
                    backgroundColor: 'transparent',
                    margin: 0
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& .MuiAutocomplete-input': {
                    marginBottom: '0px !important'
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px 0',
                    fontSize: '14px',
                    lineHeight: '1'
                  }
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  '&.Mui-disabled': {
                    opacity: 0.5,
                    color: colors.labelGrey
                  }
                }}
              >
                <Text
                  variant="body2"
                  sx={{
                    lineHeight: '1',
                    color: option.disabled ? colors.labelGrey : 'inherit'
                  }}
                >
                  {option.label}
                </Text>
              </Box>
            )}
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                paddingRight: '40px !important'
              }
            }}
          />
        </FormControl>
      </Box>
    </Box>
  );
};

type Props<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  options: string[] | DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  transformValue?: (value: string) => string | number;
  getMappingOption?: (value: string) => string;
  sx?: Record<string, any>;
};

/**
 * A dropdown component that uses react-hook-form's Controller
 * to directly bind to a specific path in the form schema
 */
function PathControlledDropdown<T extends FieldValues>({
  name,
  label,
  options,
  placeholder,
  disabled = false,
  transformValue,
  sx,
  getMappingOption
}: Props<T>) {
  const { control } = useFormContext<T>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <ControlledDropdown
          label={label}
          value={value || ''}
          onChange={disabled ? () => {} : (newValue) => onChange(transformValue ? transformValue(newValue) : newValue)}
          options={options}
          placeholder={placeholder}
          getMappingOption={getMappingOption}
          sx={sx}
          error={!!error}
        />
      )}
    />
  );
}

export default PathControlledDropdown;
